import { DndContext, DragEndEvent } from '@dnd-kit/core'
import { DateTime, Interval } from 'luxon'
import { useMemo, useRef, useState } from 'react'
import { DialogTrigger, MenuTrigger } from 'react-aria-components'
import { toast } from 'react-toastify'
import { ACCOUNT_PERMISSION } from '../../shared/constants'
import {
	Location,
	ObjectProperties,
	PartialExcept,
	Reservation,
	SavedView,
} from '../../shared/types'
import { useHasAccountPermission } from '../account-permissions'
import { DashboardEntry, useDashboardList, useDashboardUpdate } from '../api/dashboard'
import { useObjectList, useObjectListAll } from '../api/object'
import { useSavedViewListAll } from '../api/saved-views'
import { Button } from '../components/button'
import {
	DateRange,
	DateRangePicker,
	dateRangeToDateDateRange,
	dateRangeToPrimitive,
} from '../components/daterange-picker'
import { Menu, MenuItem, Popover } from '../components/menu'
import { RemoveViewModal, UpdateViewModal } from '../components/saved-view-modals'
import { Sidepanel } from '../components/sidepanel'
import { useIgnoreRentner } from '../ignore-rentner'
import { quickDeepEqual } from '../query-deep-equal'
import { useLocalStorage } from '../use-local-storage'
import { dragModifiers } from './drag-modifiers'
import { NewReservation } from './new-reservation'
import { DashboardRow } from './row'
import { OverviewSidepanel } from './sidepanel'
import { Ticks } from './ticks'
import { FilterWithProperties } from './types'
import { getDropTimes, getOffset, moveRange } from './utils'

const DEFAULT_RANGE = {
	start: 'now/d',
	end: 'now+90d/d',
}

export function Overview() {
	const [range = DEFAULT_RANGE, setRange] = useLocalStorage<DateRange>(
		'dashboard:range',
		DEFAULT_RANGE,
	)
	const [filter = {}, setFilter] = useLocalStorage<FilterWithProperties>('dashboard:filter')
	const ignoreRentner = useIgnoreRentner()
	const { locationFrom, locationTo, properties, viewId } = filter

	const dateDateRange = dateRangeToDateDateRange(range)
	const maybeInterval = Interval.fromDateTimes(dateDateRange.start, dateDateRange.end)
	const lastValidInterval = useRef<Interval<true> | null>(null)
	if (maybeInterval.isValid) {
		lastValidInterval.current = maybeInterval
	}
	const interval = lastValidInterval.current!
	const apiRange = dateRangeToPrimitive(range)

	const { data, dataUpdatedAt } = useDashboardList(apiRange.start, apiRange.end, {
		locationFrom,
		locationTo,
		ignoreRentner,
	})
	const { data: savedViews = [] } = useSavedViewListAll('dashboard')
	const view = useMemo(() => {
		if (viewId) return savedViews?.find(savedView => savedView.id === viewId)
		return savedViews?.find(savedView =>
			quickDeepEqual(savedView.filter, { locationFrom, locationTo, properties }),
		)
	}, [viewId, savedViews, locationFrom, locationTo, properties])

	const setView = (viewId: string) => {
		const view = savedViews?.find(savedView => savedView.id === viewId)
		if (view) {
			setFilter({ ...view.filter, viewId: view.id })
		}
	}

	const createMoveRange = (direction: 'plus' | 'minus') => () => setRange(moveRange(direction))

	const [viewModal, setViewModal] = useState<[SavedView, 'rename' | 'delete'] | null>(null)

	const canEditViews = useHasAccountPermission(ACCOUNT_PERMISSION.EDIT_DASHBOARD_VIEWS)
	const canRemoveViews = useHasAccountPermission(ACCOUNT_PERMISSION.DELETE_DASHBOARD_VIEWS)

	return (
		<>
			<div className="flex justify-start items-end gap-4">
				<Button variant="SECONDARY" onPress={createMoveRange('minus')}>
					◀ Předchozí
				</Button>
				<DateRangePicker value={range} onChange={setRange} label="Období" />
				<Button
					variant="TEXT"
					size="TEXT"
					onPress={() => setRange({ start: 'now/d', end: 'now+30d/d' })}
				>
					30 dní
				</Button>
				<Button
					variant="TEXT"
					size="TEXT"
					onPress={() => setRange({ start: 'now/d', end: 'now+60d/d' })}
				>
					60 dní
				</Button>
				<Button variant="SECONDARY" onPress={createMoveRange('plus')}>
					Další ▶
				</Button>
				<div className="ml-auto">
					<DialogTrigger>
						<Button variant="SECONDARY">Filrovat</Button>
						<Sidepanel heading="Filtrovat">
							{({ close }) => (
								<OverviewSidepanel
									onClose={close}
									filter={filter}
									onSubmit={setFilter}
									view={view}
								/>
							)}
						</Sidepanel>
					</DialogTrigger>
				</div>
			</div>
			<div className="flex flex-wrap items-center my-4 gap-1">
				<span className="mr-2">Pohledy</span>
				{savedViews.map(savedView => (
					<div
						key={savedView.id}
						className={
							canEditViews || canRemoveViews ? 'rounded-md hover:bg-blue-700/10' : ''
						}
					>
						<Button
							onPress={() =>
								savedView.id === view?.id ? setFilter({}) : setView(savedView.id)
							}
							variant="TEXT"
							size="SMALL"
							isActive={savedView.id === view?.id}
							className={
								canEditViews || canRemoveViews ? `rounded-l-md !px-2` : 'rounded-md'
							}
						>
							{savedView.name}
						</Button>
						{canEditViews || canRemoveViews ? (
							<MenuTrigger>
								<Button
									variant="TEXT"
									size="SMALL"
									isActive={savedView.id === view?.id}
									className="rounded-r-md !px-2 hover:bg-blue-200 pressed:bg-blue-200"
								>
									⋮
								</Button>
								<Popover>
									<Menu>
										{canEditViews ? (
											<MenuItem
												onAction={() => setViewModal([savedView, 'rename'])}
											>
												Přejmenovat
											</MenuItem>
										) : null}
										{canRemoveViews ? (
											<MenuItem
												onAction={() => setViewModal([savedView, 'delete'])}
											>
												Smazat
											</MenuItem>
										) : null}
									</Menu>
								</Popover>
							</MenuTrigger>
						) : null}
					</div>
				))}
			</div>
			<LoadedOverview
				interval={interval}
				data={data}
				properties={properties}
				key={dataUpdatedAt}
			/>
			{viewModal?.[1] === 'rename' && (
				<UpdateViewModal
					object="dashboard"
					view={viewModal[0]}
					onClose={() => setViewModal(null)}
				/>
			)}
			{viewModal?.[1] === 'delete' && (
				<RemoveViewModal
					object="dashboard"
					view={viewModal[0]}
					onClose={() => setViewModal(null)}
				/>
			)}
		</>
	)
}

function LoadedOverview({
	interval,
	data: initialData,
	properties,
}: {
	interval: Interval<true>
	data?: Partial<Record<string, DashboardEntry[]>>
	properties?: ObjectProperties
}) {
	const seconds = interval.toDuration('seconds').seconds
	const [containerRef, setContainerRef] = useState<HTMLDivElement | null>(null)
	const { data: vehicles } = useObjectList('vehicle', {
		page: -1,
		orderKey: 'name',
		orderDir: 'ASC',
		active: true,
		...properties,
	})
	const { data: locations = {} } = useObjectListAll('location', {
		select: (locations: Location[]) => {
			const asObject: Record<string, Location> = {}
			for (const location of locations) asObject[location.id] = location
			return asObject
		},
	})
	const [data, setData] = useState(initialData)
	const [newReservation, setNewReservation] = useState<Partial<Reservation>>()

	const { mutate } = useDashboardUpdate()
	const onDragEnd = ({ active, delta, over }: DragEndEvent) => {
		if (!active?.data?.current) return

		const { type, entry } = active.data.current as
			| {
					type: 'left' | 'center' | 'right'
					entry: DashboardEntry
			  }
			| {
					type: 'new'
					entry: never
			  }

		if (type === 'new') {
			const { x, y } = delta
			const width = active.rect.current.initial?.width as number
			const dateFrom = interval.start.plus({ seconds: getOffset(seconds, x, width) })
			const dateTo = interval.start.plus({ seconds: getOffset(seconds, x + y, width) })
			setNewReservation({
				vehicle_id: active.id as string,
				date_from: dateFrom.toISO(),
				date_to: dateTo.toISO(),
			})
			return
		}

		if (!over) return

		const original = { ...entry }
		const payload: PartialExcept<DashboardEntry, 'id'> = {
			id: entry.id,
		}
		const revert: PartialExcept<DashboardEntry, 'id'> = {
			id: entry.id,
		}

		if (type === 'center') {
			const offset = getOffset(seconds, delta.x, over.rect.width)
			let modified = false

			if (over.id !== entry.vehicle_id) {
				payload.vehicle_id = over.id as string
				revert.vehicle_id = original.vehicle_id
				modified = true
			}

			if (offset !== 0) {
				const dateFrom = entry.dateFrom.plus({ seconds: offset })
				const dateTo = entry.dateTo.plus({ seconds: offset })
				payload.date_from = dateFrom.toISO() as string
				payload.date_to = dateTo.toISO() as string
				payload.date_from = dateFrom.toISO() as string
				payload.date_to = dateTo.toISO() as string
				revert.date_from = original.date_from
				revert.date_to = original.date_to
				modified = true
			}

			if (!modified) return
		} else {
			if (delta.x === 0) return

			const [dateFrom, dateTo] = getDropTimes({
				entry: entry,
				type: type,
				seconds,
				delta: delta.x,
				containerWidth: over.rect.width,
			})

			payload.date_from = dateFrom.toISO() as string
			payload.date_to = dateTo.toISO() as string
			revert.date_from = original.date_from
			revert.date_to = original.date_to
		}

		const previousData = data
		const onSuccess = () => {
			const onRevert = () => {
				mutate(revert, {
					onSuccess: () => {
						setData(previousData)
					},
				})
				toast.dismiss(uid)
			}
			const uid = toast.success(
				<div className="flex items-center justify-between">
					Změny uloženy{' '}
					<Button variant="SECONDARY" size="SMALL" onPress={onRevert}>
						Vrátit
					</Button>
				</div>,
			)
		}

		mutate(payload, { onSuccess })

		const mergeEnriched = (
			original: DashboardEntry,
			patch: Partial<DashboardEntry>,
		): DashboardEntry => {
			const merged = { ...original, ...patch }
			if (patch.date_from) merged.dateFrom = DateTime.fromISO(patch.date_from)
			if (patch.date_to) merged.dateTo = DateTime.fromISO(patch.date_to)
			return merged
		}

		const patch = (data: Partial<DashboardEntry>) =>
			setData(prevState => {
				if (!prevState) return prevState
				if (data.vehicle_id) {
					return {
						...prevState,
						[data.vehicle_id]: (prevState[data.vehicle_id] ?? [])
							.concat([mergeEnriched(entry, data)])
							.sort((a, b) => (a.date_from > b.date_from ? 1 : -1)),
						[entry.vehicle_id]: prevState[entry.vehicle_id]?.filter(
							e => e.id !== entry.id,
						),
					}
				}
				return {
					...prevState,
					[entry.vehicle_id]: prevState[entry.vehicle_id]?.map(e => {
						if (e.id !== entry.id) return e
						return mergeEnriched(e, data)
					}),
				}
			})

		patch(payload)
	}

	return (
		<DndContext modifiers={dragModifiers} onDragEnd={onDragEnd}>
			<div
				className="grid grid-cols-[225px_1fr] border border-slate-200 rounded-xl overflow-hidden text-xs"
				ref={setContainerRef}
			>
				<div className="border-b border-slate-200 bg-slate-100 py-3 px-4">Vozidlo</div>
				<div className="relative border-b border-slate-200 bg-slate-100">
					<Ticks interval={interval} seconds={seconds} containerRef={containerRef} />
				</div>
				{vehicles?.list?.map(vehicle => {
					const vehicleData = data?.[vehicle.id] ?? []
					return (
						<DashboardRow
							key={vehicle.id}
							vehicle={vehicle}
							locations={locations}
							list={vehicleData}
							interval={interval}
							seconds={seconds}
						/>
					)
				})}
			</div>
			{newReservation ? (
				<NewReservation
					data={newReservation}
					onClose={() => setNewReservation(undefined)}
				/>
			) : null}
		</DndContext>
	)
}
